import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { Mail, Search, Eye, CheckCircle, XCircle, AlertTriangle, Users, Building2, Clock, Filter } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmailHistoryPage() {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [templateFilter, setTemplateFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    fetchEmails();
  }, []);

  const fetchEmails = async () => {
    try {
      const response = await axios.get(`${API_URL}/emails/history`, {
        params: { limit: 200 }
      });
      setEmails(response.data);
    } catch (error) {
      console.error(/* PROD */ "Erreur lors du chargement de l'historique:", error);
      toast.error("Impossible de charger l'historique des emails");
    } finally {
      setLoading(false);
    }
  };

  const viewEmailDetail = async (emailId) => {
    setLoadingDetail(true);
    setDetailDialogOpen(true);
    try {
      const response = await axios.get(`${API_URL}/emails/history/${emailId}`);
      setSelectedEmail(response.data);
    } catch (error) {
      console.error(/* PROD */ "Erreur lors du chargement des détails:", error);
      toast.error("Impossible de charger les détails de l'email");
      setDetailDialogOpen(false);
    } finally {
      setLoadingDetail(false);
    }
  };

  const getTemplateLabel = (template) => {
    const labels = {
      custom: "Message personnalisé",
      rappel_declaration: "Rappel de déclaration",
      demande_documents: "Demande de documents",
      mise_en_demeure: "Mise en demeure",
      information_generale: "Information générale"
    };
    return labels[template] || template;
  };

  const getStatusBadge = (status) => {
    const badges = {
      sent: { icon: CheckCircle, color: "text-green-600 bg-green-50", label: "Envoyé" },
      partial: { icon: AlertTriangle, color: "text-yellow-600 bg-yellow-50", label: "Partiel" },
      failed: { icon: XCircle, color: "text-red-600 bg-red-50", label: "Échec" }
    };
    const badge = badges[status] || badges.sent;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const filteredEmails = emails.filter((email) => {
    const matchesSearch =
      email.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      email.sender_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      email.recipients?.some(r => 
        r.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    
    const matchesTemplate = templateFilter === "all" || email.template === templateFilter;
    const matchesStatus = statusFilter === "all" || email.status === statusFilter;
    
    return matchesSearch && matchesTemplate && matchesStatus;
  });

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "dd MMM yyyy 'à' HH:mm", { locale: fr });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 p-6" data-testid="email-history-loading">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-10 w-32" />
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="email-history-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Mail className="w-6 h-6 text-[#1B4D3E]" />
            Historique des Emails
          </h1>
          <p className="text-gray-500 mt-1">
            {emails.length} email{emails.length > 1 ? "s" : ""} envoyé{emails.length > 1 ? "s" : ""}
          </p>
        </div>
        <Button onClick={fetchEmails} variant="outline" data-testid="refresh-btn">
          Actualiser
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Rechercher par sujet, expéditeur ou destinataire..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={templateFilter} onValueChange={setTemplateFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="template-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Type de template" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                <SelectItem value="custom">Message personnalisé</SelectItem>
                <SelectItem value="rappel_declaration">Rappel déclaration</SelectItem>
                <SelectItem value="demande_documents">Demande documents</SelectItem>
                <SelectItem value="mise_en_demeure">Mise en demeure</SelectItem>
                <SelectItem value="information_generale">Information</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-36" data-testid="status-filter">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="sent">Envoyé</SelectItem>
                <SelectItem value="partial">Partiel</SelectItem>
                <SelectItem value="failed">Échec</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Email List */}
      <Card>
        <CardHeader>
          <CardTitle>Emails envoyés</CardTitle>
          <CardDescription>
            Liste de tous les emails envoyés depuis la plateforme
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredEmails.length === 0 ? (
            <div className="text-center py-12 text-gray-500" data-testid="no-emails">
              <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">Aucun email trouvé</p>
              <p className="text-sm">
                {searchTerm || templateFilter !== "all" || statusFilter !== "all"
                  ? "Essayez de modifier vos filtres de recherche"
                  : "L'historique des emails envoyés apparaîtra ici"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Expéditeur</TableHead>
                    <TableHead>Sujet</TableHead>
                    <TableHead>Destinataires</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEmails.map((email) => (
                    <TableRow key={email.id} data-testid={`email-row-${email.id}`}>
                      <TableCell className="whitespace-nowrap">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Clock className="w-4 h-4" />
                          {formatDate(email.sent_at)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{email.sender_name || "Inconnu"}</div>
                        <div className="text-xs text-gray-500">{email.sender_role}</div>
                      </TableCell>
                      <TableCell className="max-w-xs truncate font-medium">
                        {email.subject}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Users className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">{email.recipients?.length || 0}</span>
                          <span className="text-gray-500 text-sm">
                            destinataire{email.recipients?.length > 1 ? "s" : ""}
                          </span>
                        </div>
                        {email.success_count !== undefined && (
                          <div className="text-xs text-gray-500">
                            {email.success_count} réussi{email.success_count > 1 ? "s" : ""}
                            {email.fail_count > 0 && `, ${email.fail_count} échec${email.fail_count > 1 ? "s" : ""}`}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="px-2 py-1 bg-gray-100 rounded text-xs">
                          {getTemplateLabel(email.template)}
                        </span>
                      </TableCell>
                      <TableCell>{getStatusBadge(email.status)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewEmailDetail(email.id)}
                          data-testid={`view-email-${email.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Voir
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#1B4D3E]" />
              Détails de l'email
            </DialogTitle>
            <DialogDescription>
              Informations complètes sur l'email envoyé
            </DialogDescription>
          </DialogHeader>

          {loadingDetail ? (
            <div className="space-y-4 py-4">
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          ) : selectedEmail ? (
            <div className="space-y-6 py-4" data-testid="email-detail">
              {/* Meta info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Date d'envoi:</span>
                  <p className="font-medium">{formatDate(selectedEmail.sent_at)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Expéditeur:</span>
                  <p className="font-medium">{selectedEmail.sender_name}</p>
                  <p className="text-xs text-gray-500">{selectedEmail.sender_role}</p>
                </div>
                <div>
                  <span className="text-gray-500">Type:</span>
                  <p className="font-medium">{getTemplateLabel(selectedEmail.template)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Statut:</span>
                  <p>{getStatusBadge(selectedEmail.status)}</p>
                </div>
              </div>

              {/* Subject */}
              <div>
                <span className="text-gray-500 text-sm">Sujet:</span>
                <p className="font-semibold text-lg">{selectedEmail.subject}</p>
              </div>

              {/* Content */}
              <div>
                <span className="text-gray-500 text-sm">Contenu:</span>
                <div className="mt-2 p-4 bg-gray-50 rounded-lg border whitespace-pre-wrap text-sm">
                  {selectedEmail.content}
                </div>
              </div>

              {/* Recipients */}
              <div>
                <span className="text-gray-500 text-sm">
                  Destinataires ({selectedEmail.recipients?.length || 0}):
                </span>
                <div className="mt-2 max-h-48 overflow-y-auto space-y-2">
                  {selectedEmail.recipients?.map((recipient, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-gray-50 rounded border"
                    >
                      <div className="flex items-center gap-2">
                        {recipient.type === "cabinet" ? (
                          <Building2 className="w-4 h-4 text-blue-500" />
                        ) : (
                          <Users className="w-4 h-4 text-green-500" />
                        )}
                        <div>
                          <p className="font-medium text-sm">{recipient.name}</p>
                          <p className="text-xs text-gray-500">{recipient.email}</p>
                        </div>
                      </div>
                      {selectedEmail.results && (
                        <span>
                          {selectedEmail.results.find(r => r.email === recipient.email)?.status === "sent" ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-500" />
                          )}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Results summary */}
              {selectedEmail.success_count !== undefined && (
                <div className="flex items-center gap-4 p-3 bg-gray-100 rounded-lg">
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">{selectedEmail.success_count} réussi{selectedEmail.success_count > 1 ? "s" : ""}</span>
                  </div>
                  {selectedEmail.fail_count > 0 && (
                    <div className="flex items-center gap-2 text-red-600">
                      <XCircle className="w-5 h-5" />
                      <span className="font-medium">{selectedEmail.fail_count} échec{selectedEmail.fail_count > 1 ? "s" : ""}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
